import React from "react";
import { groupBy } from "shared/src/util.mjs";
import * as d from "shared/src/date.mjs";
import { useLatest } from "../../hooks";
import { useCurrentLocation } from "../LocationSelector";
import * as api from "../../services/api";
import { duration, msToM } from "./fn.mjs";
import * as f from "../../utils/formatter";

function getEmployeeTimeSpent(employee) {
  const ms = employee.times_spent.reduce((acc, ts) => {
    const from = d.parseUtcDateTime(ts.from);
    const to = d.parseUtcDateTime(ts.to);

    return acc + (to - from);
  }, 0);

  return msToM(ms);
}

function getEmployeeStatusSinceLast(employee, now) {
  const last = employee.times_spent[0];
  if (last == null) {
    return 0;
  }

  return duration(d.parseUtcDateTime(last.to), now);
}

function compareNurseOption(o1, o2) {
  return o1.time_spent - o2.time_spent;
}

async function fetchEmployees(variables, signal) {
  const res = await api.availableFloorManagerNurses(variables, { signal });

  return res.data.employees.data.map((e) => ({ ...e, name: f.fullName(e) }));
}

function useEmployees(roles) {
  const now = new Date();
  const location_id = useCurrentLocation();
  const [nurses, setNurses] = React.useState([]);
  const res = React.useMemo(() => {
    return { nurses, loading: nurses == null };
  }, [nurses]);

  useLatest(
    async (signal) => {
      setNurses(null);
      if (location_id == null) {
        setNurses([]);
        return;
      }

      setNurses(
        await fetchEmployees(
          {
            day: d.toDateString(now),
            now: d.toString(now),
            location_id: location_id,
            filter: {
              shift: {
                from: {
                  gte: d.toString(d.startOfDay(now)),
                  lte: d.toString(now),
                },
              },
              or: roles.map((role) => ({
                roles: {
                  location_id,
                  role,
                },
              })),
            },
            order: { asc: "last_name" },
          },
          signal,
        ),
      );
    },
    [location_id, roles],
  );

  return res;
}

function useNurseOptions(roles) {
  const { nurses, loading } = useEmployees(roles);
  const availableOptions = React.useMemo(() => {
    const now = new Date();

    return (nurses ?? [])
      .map((n) => ({
        ...n,
        time_spent: getEmployeeTimeSpent(n),
        since_last: getEmployeeStatusSinceLast(n, now),
      }))
      .sort(compareNurseOption);
  }, [nurses]);

  return { options: availableOptions, loading };
}

/**
 * @param {Record<string, Appointment[]} appointmentsByCalendarId
 */
export function useNurseOptionsGrouped(roles) {
  const { options, loading } = useNurseOptions(roles);
  const grouped = React.useMemo(
    () => groupBy(options, (employee) => employee.status),
    [options],
  );

  return { options: grouped, loading, flatOptions: options };
}
